import { createColumnHelper } from '@tanstack/react-table';

import { ImpactReport } from 'types/report';
import { calculateLost } from 'utils/calculateLost';

const col = createColumnHelper<ImpactReport>();

/**
 * Both the row-click (edit modal) and the PDF/CSV actions need the report
 * enriched with a freshly computed single_use_items — kept in one place so
 * the two call sites can't drift.
 */
export const buildEnhancedImpactReport = (
  report: ImpactReport,
): ImpactReport => {
  const calculatedSingleUseItems =
    report?.details?.reduce(
      (acc, item) => acc + item.ret + calculateLost(item),
      0,
    ) || 0;

  return {
    ...report,
    impact: {
      ...report.impact,
      single_use_items: calculatedSingleUseItems,
    },
  };
};

/** '3/2026' -> '03/2026'; blank when either the month or year is missing. */
export const assignedMonth = (report: ImpactReport) =>
  report?.impact?.month && report?.impact?.year
    ? report.impact.month < 10
      ? `0${report.impact.month}/${report.impact.year}`
      : `${report.impact.month}/${report.impact.year}`
    : '';

export const buildImpactReportColumns = () => [
  col.accessor((report) => report?.site?.name || '-', {
    id: 'site',
    header: 'Site',
    minSize: 160,
    meta: { sortField: 'site.name' },
  }),
  col.accessor((report) => report?.impact?.name || '-', {
    id: 'name',
    header: 'Name',
    minSize: 150,
  }),
  col.accessor((report) => assignedMonth(report), {
    id: 'assignedMonth',
    header: 'Assigned Month',
    minSize: 130,
    meta: { align: 'center' },
  }),
  col.accessor((report) => report?.impact?.wd || '-', {
    id: 'waste',
    header: 'Waste',
    minSize: 90,
    meta: { align: 'center' },
  }),
  col.accessor((report) => report?.impact?.co2m || '-', {
    id: 'co2',
    header: 'CO2',
    minSize: 90,
    meta: { align: 'center' },
  }),
  col.accessor((report) => report?.impact?.wc || '-', {
    id: 'water',
    header: 'Water',
    minSize: 90,
    meta: { align: 'center' },
  }),
  col.accessor((report) => report?.impact?.er || '-', {
    id: 'energy',
    header: 'Energy',
    minSize: 90,
    meta: { align: 'center' },
  }),
  col.accessor((report) => report?.impact?.cos || '-', {
    id: 'crude',
    header: 'Crude',
    minSize: 90,
    meta: { align: 'center' },
  }),
];
